import { memo } from "react"
import { get } from "lodash"
import { Box, Typography } from "@mui/material"
import { KeyboardArrowLeft } from "@mui/icons-material"
import PropTypes from "prop-types"
import ContributorsTableTooltip from "renderers/contributors-table/tooltip"
import IntradayFreshnessDot from "components/intraday/intraday-freshness-dot"
import AggregationTableDropdown from "../aggregation-table-dropdown"
import LabelCellContent from "./label-cell-content"
import { canContributorOpenDrilldown } from "../../helpers/can-contributor-open-drilldown"

// Hovering the first column must surface only the cell's own tooltip (factor name / position
// info), not the row-level drilldown tooltip. The row tooltip opens off the GridRow's bubbling
// onMouseOver, so stopping it here keeps the two from colliding over the label.
const stopRowTooltipPropagation = (event) => event.stopPropagation()

const LabelCell = memo(({
  params,
  handleClickContributor,
  includeDrilldownArrows,
  isTopLevel,
  handleSetTableLevel,
  dropdownTableTitle,
  parentLevelOptions,
  currentGroupId,
  onDrilldownRowClick,
  intradayPricePrefix,
}) => {
  const { contributor, isAggregation, label } = params?.row || {}

  if (isAggregation) {
    return (
      <Box className="label-cell label-cell-aggregation">
        {intradayPricePrefix && <IntradayFreshnessDot priceAsOf={null} />}
        {isTopLevel ? (
          <Typography variant="body2" className="label-cell-typography">
            {label}
          </Typography>
        ) : (
          <Box className="label-cell-drill-down drill-down-link">
            <a onClick={() => handleSetTableLevel(null)} className="back-button">
              <KeyboardArrowLeft />
            </a>
            <AggregationTableDropdown
              selectedItemId={currentGroupId || ""}
              parentLevelOptions={parentLevelOptions || {}}
              dropdownTableTitle={dropdownTableTitle || "Groups"}
              label={label}
              onItemClick={(itemId, replaceLastSelectedItem) => handleSetTableLevel(itemId, replaceLastSelectedItem)}
            />
          </Box>
        )}
      </Box>
    )
  }

  const canDrilldown = Boolean(onDrilldownRowClick && canContributorOpenDrilldown(contributor))

  // The drilldown button renders after the tooltip wrapper so it sits to the right of the cell-tooltip-info icon (e.g. on Beta rows).
  return (
    <Box className="label-cell" onMouseOver={stopRowTooltipPropagation}>
      {intradayPricePrefix && <IntradayFreshnessDot priceAsOf={get(contributor?.values, [intradayPricePrefix, "priceAsOf"])} />}
      <ContributorsTableTooltip item={contributor}>
        <LabelCellContent
          contributor={contributor}
          handleClickContributor={handleClickContributor}
          includeDrilldownArrows={includeDrilldownArrows}
          canDrilldown={canDrilldown}
        />
      </ContributorsTableTooltip>
    </Box>
  )
})

LabelCell.displayName = "LabelCell"

LabelCell.propTypes = {
  params: PropTypes.shape({
    row: PropTypes.shape({
      contributor: PropTypes.object,
      isAggregation: PropTypes.bool,
      label: PropTypes.string,
    }).isRequired,
  }).isRequired,
  handleClickContributor: PropTypes.func.isRequired,
  includeDrilldownArrows: PropTypes.bool,
  isTopLevel: PropTypes.bool.isRequired,
  handleSetTableLevel: PropTypes.func.isRequired,
  dropdownTableTitle: PropTypes.string,
  parentLevelOptions: PropTypes.array,
  currentGroupId: PropTypes.string,
  onDrilldownRowClick: PropTypes.func,
  intradayPricePrefix: PropTypes.string,
}

export default LabelCell
